// Usage:
// await wait(1.5);
import { AxiosError } from "axios";
import axiosRetry from "axios-retry";
import { toRaw, isRef, isReactive, isProxy } from "vue";

export function wait(time: number): Promise<void> {
  const seconds = Math.max(0, time);
  // if (seconds > 0) {
  //   console.log(`wait ${seconds} seconds`);
  // }
  return new Promise((resolve) => setTimeout(resolve, seconds * 1000));
}

export function inElectronApp(): boolean {
  return typeof window.desktop === "object";
}

export function eventHasCmdOrCtrlKey(event: MouseEvent): boolean {
  if (navigator.platform === "MacIntel") {
    // "MacIntel" is also returned on Apple-Silicon macs…
    return event.metaKey; // the command-key on macOS.
  }
  return event.ctrlKey;
}

export function pluralize(count: number, singular: string, plural?: string): string {
  if (count === 1) return `${count} ${singular}`;
  const pluralWord = plural || `${singular}s`;
  return `${count} ${pluralWord}`;
}

export function formatSecondsAsString(seconds: number): string {
  if (seconds === Infinity || Number.isNaN(seconds)) {
    return "";
  }
  return new Date(seconds * 1000).toISOString().substr(14, 5).replace(/^0/, "");
}

export function numberToHumanDuration(seconds: number): string {
  if (seconds < 0) {
    return "";
  }

  if (seconds >= 86400 * 15 * 30.416667) {
    return `About ${pluralize(Math.round(seconds / (86400 * 15 * 30.416667)), "year")}`;
  }
  if (seconds >= 86400 * 40) {
    return `About ${pluralize(Math.round(seconds / (86400 * 30)), "month")}`;
  }
  if (seconds >= 86400 * 7) {
    return `About ${pluralize(Math.round(seconds / (86400 * 7)), "week")}`;
  }
  if (seconds >= 86400 * 1.5) {
    return `About ${pluralize(Math.round(seconds / 86400), "day")}`;
  }
  if (seconds >= 3600) {
    return `About ${pluralize(Math.round(seconds / 3600), "hour")}`;
  }
  if (seconds >= 80) {
    return `About ${pluralize(Math.round(seconds / 60), "minute")}`;
  }
  if (seconds >= 50) {
    return `About a minute`;
  }
  if (seconds >= 30) {
    return "Less than a minute";
  }
  if (seconds >= 3) {
    return `About ${pluralize(Math.ceil(seconds / 5) * 5, "second")}`;
  }
  return "Instantly";
}

export function formatMillisecondsForHumans(milliseconds: number): string {
  let remaining = Math.floor(milliseconds / 1000);
  const segments: string[] = [];
  const days = Math.floor(remaining / 86400);
  if (days) {
    remaining -= days * 86400;
    segments.push(pluralize(days, "day"));
  }
  const hours = Math.floor(remaining / 3600);
  if (hours) {
    remaining -= hours * 3600;
    segments.push(pluralize(hours, "hour"));
  }
  const minutes = Math.floor(remaining / 60);
  if (minutes) {
    remaining -= minutes * 60;
    segments.push(pluralize(minutes, "minute"));
  }
  const seconds = remaining % 60;
  if (seconds) {
    remaining -= seconds;
    segments.push(pluralize(seconds, "second"));
  }
  if (segments.length === 0) return "less than a second";
  return segments.join(", ");
}

export function sleep(seconds: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, seconds * 1000));
}

export function deepToRaw<T extends Record<string, any>>(sourceObj: T): T {
  const objectIterator = (input: any): any => {
    if (Array.isArray(input)) {
      return input.map((item) => objectIterator(item));
    }
    if (isRef(input) || isReactive(input) || isProxy(input)) {
      return objectIterator(toRaw(input));
    }
    if (input && typeof input === "object") {
      return Object.keys(input).reduce((acc, key) => {
        acc[key as keyof typeof acc] = objectIterator(input[key]);
        return acc;
      }, {} as T);
    }
    return input;
  };

  return objectIterator(sourceObj);
}

export function isAxiosErrorRecoverableForSavingEvents(error: AxiosError): boolean {
  // By default axiosRetry only retries network errors and idempotent requests (GET, HEAD, OPTIONS,
  // PUT, DELETE).
  // But we want also want to retry POSTs, except when the POST results in a 524 error from CloudFlare,
  // because that means the request was received by the server but the server took too long to respond
  // to CloudFlare (over 100 seconds).
  // https://developers.cloudflare.com/support/troubleshooting/cloudflare-errors/troubleshooting-cloudflare-5xx-errors/#error-524-a-timeout-occurred
  // For event saving, it’s risky to retry a POST request because it could result in duplicate events. For the case of
  // 524 errors, it’s likely that the 524 error was caused by the saving taking a long time on the server (in the
  // database for example), so retrying the request would result in a duplicate event.
  return (
    axiosRetry.isNetworkOrIdempotentRequestError(error) ||
    (axiosRetry.isRetryableError(error) &&
      error.config?.method === "post" &&
      error.response?.status !== 524)
  );
}
